<template>
  <div class="h-full text-gray-800 dark:text-gray-100 relative flex flex-col">

    <div class="bg-red-800 px-6 text-white flex items-center text-sm py-2 fixed bottom-0 left-0 z-50 w-full" v-if="showBanner && session.account && (session.account.role == 'admin' || session.account.role == 'manager')">
      <div class="mr-2"><img src="https://share.eins.io/star.svg" class="w-12" /></div>

      <div class="py-2 px-4 max-w-5xl mr-4">
        <p class="mb-2 font-medium">Vielen Dank für die Zusammenarbeit im Jahr 2023.</p>

        Zum Jahreswechsel führen wir <span class="font-medium">
        Abwesenheitskonten</span> und <span class="font-medium">wochentagsspezifische Sollstunden</span> ein. Dies führt zu Änderungen, wie bestehende Abwesenheiten verwaltet und neue erstellt werden.
      </div>

      <div class="flex">
        <div class="bg-transparent text-white inline-block rounded-full px-4 py-2 font-medium cursor-pointer hover:bg-white/20 mr-4" @click="showBanner = false">Schließen</div>
        <a class="bg-white text-red-800 inline-block rounded-full px-4 py-2 font-medium cursor-pointer hover:opacity-80" href="https://go.eins.io/zeit-release-2024-01" target="_blank">Erfahre&nbsp;mehr</a>
      </div>
    </div>

    <div class="flex w-full max-w-full h-full" v-if="!isMobile && session.account && ($route.meta.hideChrome || !showBottomNavbar)">

      <DesktopMenu v-if="!showBottomNavbar && !$route.meta.hideChrome" />

      <div class="relative flex-grow h-full overflow-auto">
        <router-view />
      </div>
    </div>
    <zeit-tab-menu v-else />

    <zeit-notifications />


    <pre class="hidden z-50 bottom-0 left-0 w-full bg-black text-white px-2 py-1 text-xs">sbn: {{ showBottomNavbar }}
$route.meta.hideChrome: {{ $route.meta.hideChrome || false }}</pre>

    <zeit-help-sidebar v-if="!isMobile && false" />
    
    <InactiveLicensePrompt v-if="session.institution" />
    <ExpiringLicensePrompt v-if="session.institution" />
    <SwitchToBasicPrompt v-if="session.institution" />
  </div>
</template>

<style scoped>

/* Deprecated Ionic Styles */
ion-split-pane {
  --side-max-width: 20%;
  --side-width: 100%;
  --side-min-width: 100%;
}

ion-split-pane.split-pane-visible {
  --side-min-width: 200px;
}

ion-menu {
  --max-width: 20%;
  --width: 100%;
  --min-width: 300px;
}

ion-menu.menu-pane-visible {
  --max-width: 100%;
  --min-width: 200px;
}

.flip-y {
  transform: scaleY(-1);
}

ion-list {
  padding-top: 0 !important;
  padding-bottom: 1.5rem !important;
}

zeit-tab-menu.hide,
ion-menu.hide {
  pointer-events: none;
  display: none;
}

ion-list {
  --ion-item-background: 'transparent';
}

ion-list.md ion-item {
  margin-left: -10px;
}
</style>

<style>
html.font-size-terminal {
  font-size: 125%;
}

.max-w-48 {
  max-width: 12rem;
}
</style>


<script lang="ts">
import { IonApp, IonIcon, IonLabel, IonList, isPlatform, menuController, } from '@ionic/vue';
import { defineComponent, ref } from 'vue';
import { useRoute } from 'vue-router';
import {
  logOutOutline, archiveOutline, folderOpenOutline, peopleOutline, peopleCircle, gridOutline, desktopOutline, archiveSharp,
  bookmarkOutline, bookmarkSharp, heartOutline, heartSharp, mailOutline, mailSharp, paperPlaneOutline, paperPlaneSharp,
  trashOutline, trashSharp, warningOutline, warningSharp, helpCircleOutline, timerOutline,
  calendarNumberOutline, glassesOutline, cogOutline, playCircleOutline,
} from 'ionicons/icons';

import { accountService } from '../../src/services/accounts';
import { institutionService } from '../../src/services/institutions';
import ZeitHelpSidebar from '@/components/ui/ZeitHelpSidebar.vue';
import ZeitLogo from '../../src/components/graphics/Logo.vue';
import ZeitNotifications from '../../src/components/ui/ZeitNotifications.vue';
import ZeitTabMenu from '../../src/components/ui/ZeitTabMenu.vue';
import DesktopMenu from '../../src/components/navigation/DesktopMenu.vue';

import branding from '../../src/branding';
import { updateStatusBar, isDark } from "../../src/globals/statusbar";
import { showBottomNavbar } from "../../src/globals/refs";
import { isAuthenticated } from "../../src/globals/helpers";

import { ScreenOrientation } from '@ionic-native/screen-orientation';

import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { StatusBar } from '@capacitor/status-bar';
import { session } from '../../src/globals/session';
import { trackingService } from '../../src/services/tracking';
import InactiveLicensePrompt from '../../src/components/ui/InactiveLicensePrompt.vue';
import ExpiringLicensePrompt from '../../src/components/ui/ExpiringLicensePrompt.vue';
import SwitchToBasicPrompt from '../../src/components/ui/SwitchToBasicPrompt.vue';

import { initIntercom } from '@/globals/intercom';
import { initRecorder } from '@/globals/recorder';

interface Page {
  title: string;
  url: string;
  iosIcon: any;
  mdIcon: any;
  permissions?: Array<string>;
  roles?: Array<string>;
  showForMobile?: boolean;
}

interface Category {
  label: string;
  pages: Array<Page>;
}


export default defineComponent({
  name: 'App',
  components: {
    DesktopMenu,
    IonApp,
    // IonContent,
    IonIcon,
    // IonItem,
    IonLabel,
    IonList,
    // IonText,
    // IonMenu,
    // IonMenuToggle,
    // IonRouterOutlet,
    // IonSplitPane,
    // BackgroundDrawingBottom,
    // ZeitAvatar,
    ZeitTabMenu,
    ZeitLogo,

    ZeitNotifications,
    ZeitHelpSidebar,
    InactiveLicensePrompt,
    ExpiringLicensePrompt,
    SwitchToBasicPrompt,
  },
  setup() {

    initIntercom();
    initRecorder();

    const selectedIndex = ref(0);
    const route = useRoute();

    return {
      selectedIndex,
      archiveOutline,
      archiveSharp,
      bookmarkOutline,
      bookmarkSharp,
      logOutOutline,
      peopleOutline,
      gridOutline,
      desktopOutline,
      heartOutline,
      heartSharp,
      mailOutline,
      mailSharp,
      folderOpenOutline,
      paperPlaneOutline,
      paperPlaneSharp,
      trashOutline,
      trashSharp,
      warningOutline,
      warningSharp,
      accountService,
      institutionService,

      isSelected: (url: string) => url === route.path ? 'selected' : '',
      session: session,
    }
  },
  data() {
    return {
      isMobile: branding.isMobile,
      showBanner: false,

      showBottomNavbar,

      branding,
      isPlatform,
    }
  },
  provide() {
    return {
      isMobile: branding.isMobile,
      isAndroid: isPlatform("android"),
      isTerminal: false,
    }
  },
  methods: {
    updateOSChrome(transparentStatusBar: boolean = false) {
      updateStatusBar({
        backgroundColor: isDark() ? branding.colors.darkHeaderBackground : branding.colors.lightHeaderBackground,
        backgroundBottomColor: isDark() ? branding.colors.darkHeaderBackground : '#ffffff',
        buttonsBottomDark: !isDark(),
      });

      this.updateSideMenuVisibility();
    },
    closeMenu() {
      menuController.close();
    },
    onMenuOpen() {
      if (isPlatform("capacitor")) StatusBar.hide();
    },
    onMenuClose() {
      if (isPlatform("capacitor")) StatusBar.show();
    },
    logout() {
      this.accountService.logout();
      this.$router.replace({ name: 'authentication:login' });
    },
    updateSideMenuVisibility() {
      if (!session.account) return;
      showBottomNavbar.value = branding.isMobile && session.account.role == 'employee';
    },
    processTrackingQueue() {
      trackingService.processQueue();
    },
  },
  async mounted() {
    // Check for auth query param
    const auth = this.$route.query.auth;
    if (auth) {
      localStorage.accessToken = auth;
      let accounts, institutions;
      try {
        [accounts, institutions] = await Promise.all([
          accountService.list(),
          institutionService.list(),
        ])
      } catch (error) {
        // Expired auth links return 403; the axios interceptor already clears
        // the token and redirects to login, so suppress the error here.
        if ((error as any)?.response?.status !== 403) throw error;
        return;
      }
      session.setAccount(accounts.data.results[0]);
      session.setInstitution(institutions.data.results[0]);
      window.location.href = this.$route.path;
      return;
    }

    // Initialize app
    if (isPlatform("capacitor") && isPlatform('android')) {
      Keyboard.setAccessoryBarVisible({ isVisible: false });
    }

    if (isPlatform("capacitor") && isPlatform('ios')) {
      Keyboard.setResizeMode({ mode: KeyboardResize.Body });
    }

    this.updateSideMenuVisibility();

    // Add branding to html tag
    document.getElementsByTagName("html")[0].classList.add("brand-" + branding.id);

    // Add target to html tag
    document.getElementsByTagName("html")[0].classList.add("target-" + import.meta.env.VITE_APP_TARGET);

    // Lock to portrait mode (best-effort: some WebViews don't support it)
    if (isPlatform("capacitor")) {
      ScreenOrientation.lock("portrait").catch(() => {
        // NotSupportedError on certain Android WebViews — safe to ignore.
      });
    }

    // Set status bar
    updateStatusBar({
      transparentStatusBar: this.$route.meta.transparentStatusBar as boolean | undefined,
    });

    window.matchMedia('(prefers-color-scheme: dark)').addListener(mediaQuery => this.updateOSChrome());

    document.body.classList.toggle('not-loaded', false);

    setInterval(this.processTrackingQueue, 1000);


    console.log('Running ' + branding.name + ' in version ' + import.meta.env.VITE_APP_VERSION_MARKETING + ' (' + import.meta.env.VITE_APP_VERSION_CODE + ').');
  },
});
</script>

<style scoped>
ion-menu .heading-navigation {
  font-weight: 500;
  font-size: 110%;
}

ion-menu ion-content {
  --background: var(--ion-item-background, var(--ion-background-color, #fff));
}

ion-menu.md ion-content {
  --padding-start: 8px;
  --padding-end: 8px;
  --padding-top: 20px;
  --padding-bottom: 20px;
}

ion-menu.md ion-list {
  padding: 20px 0;
}

ion-menu.md ion-list#inbox-list {
  border-bottom: 1px solid var(--ion-color-step-150, #d7d8da);
}

ion-menu.md ion-item {
  --padding-start: 10px;
  --padding-end: 10px;
  border-radius: 4px;
}

ion-menu.md ion-item.selected {
  --background: rgba(var(--ion-color-primary-rgb), 0.14);
}

ion-menu.md ion-item.selected ion-icon {
  color: var(--ion-color-primary);
}

ion-menu.md ion-item ion-icon {
  color: #616e7e;
}

ion-menu.md ion-item ion-label {
  font-weight: 500;
}

ion-menu.ios ion-content {
  --padding-bottom: 20px;
}

ion-menu.ios ion-list {
  padding: 20px 0 0 0;
}

ion-menu.ios ion-note {
  line-height: 24px;
  margin-bottom: 20px;
}

ion-menu.ios ion-item {
  --padding-start: 16px;
  --padding-end: 16px;
  --min-height: 50px;
}

ion-menu.ios ion-item.selected ion-icon {
  color: var(--ion-color-primary);
}

ion-menu.ios ion-item ion-icon {
  font-size: 18px;
  color: #73849a;
}

ion-label {
  font-size: 14px !important;
}

ion-menu.ios ion-list#labels-list ion-list-header {
  margin-bottom: 8px;
}

ion-menu.ios ion-list-header,
ion-menu.ios ion-note {
  padding-left: 16px;
  padding-right: 16px;
}

ion-menu.ios ion-note {
  margin-bottom: 8px;
}

ion-note {
  display: inline-block;
  font-size: 16px;

  color: var(--ion-color-medium-shade);
}

ion-item.selected {
  --color: var(--ion-color-primary);
}
</style>
